import React from 'react';
import { Edit, Plus, TestTube, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import InfoTip from './InfoTip';

const GROUPS = [
  { key: 'inside', title: 'Use Qflow inside other apps', tip: 'People ask Qflow and use Floor Space from another app. Each person acts as their own Qflow user, with the same permissions.' },
  { key: 'alerts', title: 'Send alerts to other apps', tip: 'Qflow posts alerts you choose (escalations, reviews due, approvals, failed refreshes) to a channel, inbox or webhook. The bell in Qflow always gets them too.' },
];

const STATUS = {
  connected: ['Connected', 'bg-emerald-100 text-emerald-800'],
  error: ['Needs attention', 'bg-amber-100 text-amber-800'],
  disconnected: ['Not tested', 'bg-gray-100 text-gray-700'],
};

const timeAgo = (value) => {
  if (!value) return '';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 60) return `${Math.max(minutes, 1)}m ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours}h ago` : new Date(value).toLocaleDateString();
};

// The Plugins page: what is connected, grouped by what it does, plus who hears about what.
const PluginsPanel = ({ integrations, templates, alertEvents, iconFor, onAdd, onEdit, onTest, onToggle, onDelete, onManage, onSetup }) => {
  const eventLabel = Object.fromEntries((alertEvents || []).map((event) => [event.key, event.label]));
  const groupOf = (item) => item.group || templates[item.type]?.group || 'alerts';
  const routing = (alertEvents || []).map((event) => ({
    ...event,
    plugins: integrations.filter((item) => item.enabled && (item.alert_events || []).includes(event.key)),
  }));

  return <div className="space-y-5" data-testid="plugins-panel">
    <p className="text-sm text-gray-600" data-testid="app-integration-capability-note">Choose only the connectors your organization uses. Nothing is connected until you add it.</p>
    <div className="grid gap-5 xl:grid-cols-2">
      {GROUPS.map((group) => {
        const configured = integrations.filter((item) => groupOf(item) === group.key);
        const available = Object.entries(templates).filter(([, template]) => (template.group || 'alerts') === group.key);
        return <Card key={group.key} className="p-4" data-testid={`plugins-group-${group.key}`}>
          <div className="mb-2 flex items-center gap-2"><h2 className="text-base font-semibold text-gray-900">{group.title}</h2><InfoTip label={`About ${group.title}`}>{group.tip}</InfoTip></div>
          <ul className="divide-y divide-gray-100">
            {configured.map((item) => {
              const [label, chip] = STATUS[item.status] || STATUS.disconnected;
              const sends = (item.alert_events || []).map((key) => eventLabel[key] || key);
              return <li key={item.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600">{iconFor(item.type)}</span>
                <div className="min-w-[12rem] flex-1">
                  <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-500">{templates[item.type]?.name || item.type}
                    {group.key === 'alerts' && <> · {sends.length ? `Sends: ${sends.join(', ')}` : 'No alerts chosen yet'}</>}
                    {item.last_delivery_at && <> · Last alert {timeAgo(item.last_delivery_at)}{item.last_delivery_status === 'failed' ? ' (failed)' : ''}</>}
                  </p>
                </div>
                <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${chip}`} title={item.last_test_message || ''}>{label}</span>
                <Switch checked={item.enabled} onCheckedChange={(checked) => onToggle(item.id, checked)} aria-label={`${item.enabled ? 'Turn off' : 'Turn on'} ${item.name}`} />
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => onTest(item.id)} aria-label={`Test ${item.name}`}><TestTube size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => onEdit(item)} aria-label={`Edit ${item.name}`}><Edit size={14} /></Button>
                  {item.type === 'teams_bot' && onSetup && <Button size="sm" variant="outline" onClick={() => onSetup(item)} data-testid="teams-setup-button">Setup</Button>}
                  {(item.type === 'qflow_api' || item.type === 'teams_bot') && <Button size="sm" variant="outline" onClick={() => onManage(item)}>{item.type === 'qflow_api' ? 'API keys' : 'User mappings'}</Button>}
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDelete(item.id)} aria-label={`Delete ${item.name}`}><Trash2 size={14} /></Button>
                </div>
              </li>;
            })}
          </ul>
          <div className={`flex flex-wrap gap-2 ${configured.length ? 'mt-2 border-t border-gray-100 pt-3' : ''}`}>
            {!configured.length && <p className="w-full text-sm text-gray-500">Nothing connected yet.</p>}
            {available.map(([key, template]) => <button key={key} type="button" onClick={() => onAdd(key, template.name)}
              className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-sky-300 hover:text-sky-700" title={template.description}>
              <Plus size={12} aria-hidden="true" />{template.name}
            </button>)}
          </div>
        </Card>;
      })}
    </div>
    {routing.length > 0 && <Card className="p-4" data-testid="plugins-alert-routing">
      <div className="mb-1 flex items-center gap-2"><h2 className="text-base font-semibold text-gray-900">Who hears about what</h2><InfoTip label="About alert routing">Choose alerts on each plugin with Edit. Alerts always appear in the Qflow bell for the right people as well.</InfoTip></div>
      <ul className="divide-y divide-gray-100">{routing.map((event) => <li key={event.key} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
        <span className="text-gray-900">{event.label}</span>
        <span className="text-gray-500">{['Qflow bell', ...event.plugins.map((item) => item.name)].join(' · ')}</span>
      </li>)}</ul>
    </Card>}
  </div>;
};

export default PluginsPanel;
